import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { DatePicker } from "../components/DatePicker.js";
import { api, ApiError, type Scope } from "../lib/api.js";
import { addDays, today } from "../lib/dates.js";
import { useIsDesktop } from "../lib/media.js";
import { loadOccurrenceOrEvent, resolveOccurrence } from "../lib/occurrence.js";
import {
  describe as describeRepeat,
  fromRrule,
  summarise,
  toRrule,
  WEEKDAY_LABELS,
  type Repeat,
} from "../lib/repeat.js";
import { describeSpan } from "../lib/span.js";
import { TimeField } from "../components/TimeField.js";
import { useSession } from "../lib/session.js";
import { foregroundOn, initialOf } from "../tokens/palette.js";

interface Draft {
  title: string;
  date: string;
  /**
   * The last day the event covers, inclusive — "back Thursday", the way
   * people say it. Equal to `date` for the ordinary one-day event, and the
   * only reason this screen can express a trip at all.
   *
   * Stored ends are exclusive; the conversion happens once, on the way out.
   */
  lastDay: string;
  start: string;
  end: string;
  allDay: boolean;
  people: string[];
  repeat: Repeat;
  location: string;
  notes: string;
}

/**
 * The full-screen editor.
 *
 * Validation runs on save only, per field, inline — no modal, and no
 * complaining while someone is still typing. The two errors that exist say what
 * is wrong and what fixes it, in the design's own words.
 */
export function Editor({
  onToast,
}: {
  onToast: (message: string, undo?: () => Promise<void>) => void;
}): React.JSX.Element {
  const { zone, people, canEdit } = useSession();
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params] = useSearchParams();

  const isNew = id === undefined || id === "new";
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ title?: boolean; people?: boolean; lastDay?: boolean }>(
    {},
  );
  const [failure, setFailure] = useState<string | null>(null);
  const [repeatOpen, setRepeatOpen] = useState(false);
  /** Which of the two date rows has the picker open, if either. */
  const [dateOpen, setDateOpen] = useState<"start" | "last" | null>(null);
  /** Set when a change or delete on a repeating event needs a scope. */
  const [asking, setAsking] = useState<"save" | "delete" | null>(null);

  const occurrenceId = params.get("occ");

  const [draft, setDraft] = useState<Draft>(() => ({
    title: "",
    date: params.get("date") ?? today(zone),
    lastDay: params.get("date") ?? today(zone),
    start: "17:00",
    end: "18:00",
    allDay: false,
    people: [],
    repeat: { kind: "none", byDay: [] },
    location: "",
    notes: "",
  }));

  /*
   * A typed-in URL should not open a form that can only fail. The server
   * refuses the save either way, but being shown the fields and then told no
   * is worse than never being offered them.
   */
  useEffect(() => {
    if (canEdit) return;
    void navigate(isNew ? "/" : `/event/${id ?? ""}`, { replace: true });
  }, [canEdit, id, isNew, navigate]);

  useEffect(() => {
    if (isNew) return;

    void (async () => {
      /*
       * Opened from a particular day, the editor loads that day — not the
       * series. It must show that occurrence's own title and notes if it has
       * been changed before, or opening an edited day and saving would quietly
       * put the series' words back.
       */
      const event = await loadOccurrenceOrEvent(id, occurrenceId);

      const { start, end } = resolveOccurrence(event, null, zone);

      const startDate = start.toISODate() ?? today(zone);
      /*
       * Ends are stored exclusive, so the last day covered is the moment
       * before the stored end. Backing off a millisecond rather than a day
       * gets both spellings right at once — the all-day trip whose end is a
       * midnight, and the flight that lands at eight in the morning.
       */
      const lastDay = end === null ? startDate : (end.minus({ milliseconds: 1 }).toISODate() ?? startDate);

      setDraft({
        title: event.title,
        date: startDate,
        lastDay: lastDay < startDate ? startDate : lastDay,
        start: start.toFormat("HH:mm"),
        end: end === null ? "" : end.toFormat("HH:mm"),
        allDay: event.all_day,
        people: event.people.map((p) => p.id),
        repeat: fromRrule(event.rrule),
        location: event.location ?? "",
        notes: event.notes ?? "",
      });
      setLoaded(true);
    })();
  }, [id, isNew, zone, occurrenceId]);

  const repeats = draft.repeat.kind !== "none";
  const spans = draft.lastDay > draft.date;

  const patch = (change: Partial<Draft>): void => {
    setDraft((current) => ({ ...current, ...change }));
  };

  /**
   * Moving the start moves the finish with it.
   *
   * Someone pushing a four-day trip back a week means the whole trip, not a
   * trip that now ends before it begins. The length is what they are holding
   * in their head; the dates are how they say it.
   */
  const moveStart = (date: string): void => {
    setDraft((current) => {
      const length = Math.max(
        0,
        DateTime.fromISO(current.lastDay, { zone })
          .diff(DateTime.fromISO(current.date, { zone }), "days").days,
      );
      return { ...current, date, lastDay: addDays(date, Math.round(length), zone) };
    });
  };

  async function save(scope?: Scope): Promise<void> {
    const next: typeof errors = {};
    if (draft.title.trim() === "") next.title = true;
    if (draft.people.length === 0) next.people = true;
    if (draft.lastDay < draft.date) next.lastDay = true;
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    // Editing one date of a repeating event is ambiguous, and guessing wrong
    // either changes a whole term of hockey or none of it. Ask.
    if (!isNew && repeats && occurrenceId !== null && scope === undefined) {
      setAsking("save");
      return;
    }

    setAsking(null);
    setSaving(true);
    setFailure(null);

    /*
     * The end is written on the last day the event covers, not on the day it
     * started. That one substitution is what makes a trip a trip: before it,
     * every event this screen produced finished the same day it began, and a
     * week in Denver could only be typed as seven separate Wednesdays.
     *
     * All-day ends go out inclusive — the API takes "back Thursday" and does
     * the exclusive-midnight arithmetic itself, in the one place that owns it.
     */
    const payload = {
      title: draft.title.trim(),
      all_day: draft.allDay,
      starts_at: draft.allDay ? draft.date : `${draft.date}T${draft.start}`,
      ends_at: draft.allDay
        ? draft.lastDay
        : draft.end === ""
          ? null
          : `${draft.lastDay}T${draft.end}`,
      location: draft.location.trim() === "" ? null : draft.location.trim(),
      notes: draft.notes.trim() === "" ? null : draft.notes.trim(),
      rrule: toRrule(draft.repeat, draft.date, zone),
      person_ids: draft.people,
    };

    try {
      if (isNew) {
        await api.createEvent(payload);
        onToast(`Added “${payload.title}”`);
        void navigate(-1);
        return;
      }

      if (scope !== undefined && occurrenceId !== null) {
        await api.updateOccurrence(id, occurrenceId, scope, {
          title: payload.title,
          starts_at: payload.starts_at,
          ends_at: payload.ends_at,
          notes: payload.notes,
        });
        onToast(scope === "this" ? "Changed just that one" : "Changed it from there on");
        // An exception is keyed on the start the rule produced, so this link
        // still points at the day that was just changed.
        void navigate(`/event/${id}/?occ=${encodeURIComponent(occurrenceId)}`.replace("/?", "?"), {
          replace: true,
        });
        return;
      }

      await api.updateEvent(id, payload);
      onToast(`Saved “${payload.title}”`);

      /*
       * Back to the event, not back through history. Changing a one-off
       * event's time moves the only occurrence it has, and the link that got
       * here names the old time — going "back" would land on a slot that no
       * longer exists and show the time nobody just typed.
       */
      void navigate(`/event/${id}`, { replace: true });
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : "That didn't save.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(scope?: Scope): Promise<void> {
    if (isNew) return;

    if (repeats && occurrenceId !== null && scope === undefined) {
      setAsking("delete");
      return;
    }

    setAsking(null);
    const event = await api.event(id);

    if (scope !== undefined && occurrenceId !== null) {
      await api.deleteOccurrence(id, occurrenceId, scope);
      onToast(scope === "this" ? "Deleted just that one" : "Deleted it from there on");
      void navigate("/");
      return;
    }

    await api.deleteEvent(id);

    onToast(`Deleted “${event.title}”`, async () => {
      await api.createEvent({
        title: event.title,
        starts_at: event.starts_at,
        ends_at: event.ends_at,
        all_day: event.all_day,
        notes: event.notes,
        location: event.location,
        rrule: event.rrule,
        person_ids: event.people.map((p) => p.id),
      });
    });

    void navigate("/");
  }

  if (!loaded) return <div className={`sheet${isDesktop ? " sheet--column" : ""}`} />;

  return (
    <div className={`sheet${isDesktop ? " sheet--column" : ""}`}>
      <header className="sheet__head">
        <button type="button" onClick={() => void navigate(-1)}>
          Cancel
        </button>
        <span className="sheet__title">{isNew ? "New event" : "Edit"}</span>
        <button type="button" className="sheet__save" disabled={saving} onClick={() => void save()}>
          Save
        </button>
      </header>

      <div className="sheet__body">
        <input
          className="field-title"
          value={draft.title}
          placeholder="What is it?"
          aria-label="Title"
          onChange={(e) => patch({ title: e.target.value })}
        />
        {errors.title === true && <p className="error">Needs a name before it can be saved.</p>}

        <p className="label">Who</p>
        <div className="who who--editor">
          {people.map((person) => {
            const on = draft.people.includes(person.id);
            return (
              <button
                key={person.id}
                type="button"
                className={`chip${on ? " is-on" : ""}`}
                aria-pressed={on}
                onClick={() =>
                  patch({
                    people: on
                      ? draft.people.filter((x) => x !== person.id)
                      : [...draft.people, person.id],
                  })
                }
                style={
                  on
                    ? {
                        background: person.color,
                        color: foregroundOn(person.color),
                        borderColor: person.color,
                      }
                    : undefined
                }
              >
                <span
                  className="disc chip__disc"
                  style={
                    on
                      ? { background: foregroundOn(person.color), color: person.color }
                      : { background: person.color, color: foregroundOn(person.color) }
                  }
                >
                  {initialOf(person.display_name)}
                </span>
                {person.display_name}
              </button>
            );
          })}
        </div>
        {errors.people === true && (
          <p className="error">Pick at least one person — that's what gives it a colour.</p>
        )}

        <div className="row">
          <span>All day</span>
          <button
            type="button"
            role="switch"
            aria-checked={draft.allDay}
            className={`switch${draft.allDay ? " is-on" : ""}`}
            onClick={() => patch({ allDay: !draft.allDay })}
          >
            <span className="switch__knob" />
          </button>
        </div>

        <button
          type="button"
          className="row row--button"
          onClick={() => setDateOpen("start")}
        >
          <span>{spans ? "First day" : "Date"}</span>
          <span className="row__value">
            {DateTime.fromISO(draft.date, { zone }).toFormat("ccc LLL d, yyyy")}
          </span>
        </button>

        {/* The row that makes a trip possible. It is always here rather than
            behind a toggle: a hidden field is a feature nobody finds, and
            "Same day" is a quiet enough answer for the events that are. */}
        <button
          type="button"
          className="row row--button"
          onClick={() => setDateOpen("last")}
        >
          <span>Last day</span>
          <span className={`row__value${spans ? "" : " is-quiet"}`}>
            {spans
              ? DateTime.fromISO(draft.lastDay, { zone }).toFormat("ccc LLL d, yyyy")
              : "Same day"}
          </span>
        </button>

        {errors.lastDay === true && <p className="error">It can&rsquo;t end before it starts.</p>}

        {spans && (
          <p className="row__note">
            {describeSpan(draft.date, draft.lastDay, zone)}
          </p>
        )}

        {!draft.allDay && (
          <>
            <TimeField
              label={spans ? "Starts (first day)" : "Starts"}
              value={draft.start}
              onChange={(start) => patch({ start })}
            />

            <TimeField
              label={spans ? "Ends (last day)" : "Ends"}
              value={draft.end}
              onChange={(end) => patch({ end })}
              allowEmpty
            />
          </>
        )}

        <button type="button" className="row row--button" onClick={() => setRepeatOpen(true)}>
          <span>Repeat</span>
          <span className="row__value">{describeRepeat(draft.repeat, draft.date, zone)}</span>
        </button>

        <label className="row row--stack">
          <span className="label">Where</span>
          <input
            value={draft.location}
            placeholder="Optional"
            onChange={(e) => patch({ location: e.target.value })}
          />
        </label>

        <label className="row row--stack">
          <span className="label">Notes</span>
          <textarea
            value={draft.notes}
            placeholder="Bring shin pads"
            rows={3}
            onChange={(e) => patch({ notes: e.target.value })}
          />
        </label>

        {failure !== null && <p className="error">{failure}</p>}

        {!isNew && (
          <button type="button" className="danger" onClick={() => void remove()}>
            {!repeats
              ? "Delete"
              : occurrenceId === null
                ? "Delete every one of these"
                : "Delete…"}
          </button>
        )}
      </div>

      {dateOpen !== null && (
        <DatePicker
          value={dateOpen === "start" ? draft.date : draft.lastDay}
          zone={zone}
          // Nothing before the first day can be the last one.
          {...(dateOpen === "last" ? { min: draft.date } : {})}
          onPick={(date) => {
            if (dateOpen === "start") moveStart(date);
            else patch({ lastDay: date });
            setDateOpen(null);
          }}
          onClose={() => setDateOpen(null)}
        />
      )}

      {asking !== null && (
        <div className="repeat" onClick={() => setAsking(null)}>
          <div className="repeat__panel" onClick={(e) => e.stopPropagation()}>
            <p className="repeat__head">
              {asking === "delete" ? "Delete which?" : "Change which?"}
            </p>
            <p className="repeat__summary">This one repeats.</p>

            <button
              type="button"
              className="repeat__option"
              onClick={() => void (asking === "delete" ? remove("this") : save("this"))}
            >
              <span>Just this one</span>
            </button>

            <button
              type="button"
              className="repeat__option"
              onClick={() =>
                void (asking === "delete"
                  ? remove("this_and_future")
                  : save("this_and_future"))
              }
            >
              <span>This one and every one after it</span>
            </button>

            <button type="button" className="linkish" onClick={() => setAsking(null)}>
              Never mind
            </button>
          </div>
        </div>
      )}

      {repeatOpen && (
        <div className="repeat">
          <div className="repeat__panel">
            <p className="repeat__head">How often?</p>

            {(
              [
                ["none", "Doesn't repeat"],
                ["daily", "Every day"],
                ["weekly", "Weekly"],
                ["monthly", "Monthly on the Nth"],
                ["yearly", "Every year on this date"],
              ] as const
            ).map(([kind, label]) => (
              <button
                key={kind}
                type="button"
                className="repeat__option"
                onClick={() => patch({ repeat: { ...draft.repeat, kind } })}
              >
                <span>{label}</span>
                {draft.repeat.kind === kind && <span className="repeat__tick">✓</span>}
              </button>
            ))}

            {draft.repeat.kind === "weekly" && (
              <div className="repeat__days">
                {WEEKDAY_LABELS.map((name, index) => {
                  const on = draft.repeat.byDay.includes(index);
                  return (
                    <button
                      key={name}
                      type="button"
                      className={`repeat__day${on ? " is-on" : ""}`}
                      aria-pressed={on}
                      onClick={() =>
                        patch({
                          repeat: {
                            ...draft.repeat,
                            byDay: on
                              ? draft.repeat.byDay.filter((d) => d !== index)
                              : [...draft.repeat.byDay, index],
                          },
                        })
                      }
                    >
                      {name[0]}
                    </button>
                  );
                })}
              </div>
            )}

            <p className="repeat__summary">{summarise(draft.repeat, draft.date, zone)}</p>

            <button type="button" className="button-ink" onClick={() => setRepeatOpen(false)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
